import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { BHFilter } from './bh-filter.js';
import { Institution } from './institution.js';
import { User } from './user.js';

@Entity()
export class Category extends MongoEntity {
  public static override _class = 'Category';
  public static override _collectionName = 'categories';

  @Field(type => String)
  @validate(nonOptional())
  public name!: string;

  @validate(nonOptional())
  @Field(type => String)
  public institutionID!: string;

  @Field(type => Float)
  sortingWeight?: number;

  @Query(type => [Category])
  static getCategoriesByInstitution(@Arg('institutionID', type => ID) institutionID: ID) {
    return async (gqlFields: BaseQLSelectionSet<Category>) => {
      const collection = await getCollection<Category>(Category._class);
      const result = await collection.find<Category>({ institutionID }).toArray();
      return result;
    };
  }

  @Subscription(type => [Category])
  static subscribeCategories() {
    return (gqlFields: BaseQLSelectionSet<Category>) => {
      return getCollectionHandle<Category>(this._collectionName).watchQuery({ institutionID });
    };
  }

  @Subscription(type => [Category])
  static subscribeCategoriesByInstitution(@Arg('institutionID', type => ID) institutionID: ID) {
    return (gqlFields: BaseQLSelectionSet<Category>) => {
      return getCollectionHandle<Category>(this._collectionName).watchQuery({ institutionID });
    };
  }

  @Subscription(type => [Category])
  static subscribeCatgeoriesPaginated<T extends MongoEntity>(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => BHFilter) filter: BHFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<T>) => {
      return this.subscribePaginatedAggregation(skip, limit, filter.getMongoFilter(), [
        {
          $match: {
            _class: Category._class,
            ...(filter.institutionID ? { institutionID: filter.institutionID } : {}),
          },
        },
        { $sort: filter.getMongoOptions().sort },
      ]);
    };
  }

  static async getAllowedCategories() {
    // check permissions
    const userID = context.serverContext ? undefined : (context.userID as string | undefined);
    if (!userID) return [];
    const user = context.serverContext
      ? undefined
      : await CurrentUserInfo.getMyself<User>()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!user) return [];

    const institutionIDs = await Institution.getAllowedInstitutions();

    const result = await (
      await getRawCollection(Category._collectionName)
    )
      .aggregate<{ _id: string }>([
        {
          $match: {
            institutionID: {
              $in: institutionIDs,
            },
          },
        },
        {
          $project: {
            _id: 1,
          },
        },
      ])
      .toArray();

    return result.map(res => res._id);
  }
}
